import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Detective, Plus, Trash, CaretRight } from "@phosphor-icons/react";
import { DataResidencyNote } from "../components/DataResidencyNote";
import { LensScopePicker } from "../components/lens/LensScopePicker";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700",
  in_review: "bg-amber-100 text-amber-800",
  reconciling: "bg-blue-100 text-blue-800",
  complete: "bg-emerald-100 text-emerald-800",
};
const STATUS_LABEL = { draft: "Draft", in_review: "In Review", reconciling: "Reconciling", complete: "Complete" };

function ReviewRow({ r, onOpen, onDelete }) {
  return (
    <div data-testid={`rr-row-${r.id}`} className="flex items-center gap-4 border border-slate-200 bg-white px-4 py-3 hover:border-slate-400 cursor-pointer transition-colors"
         onClick={() => onOpen(r.id)}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">{r.title}</div>
        <div className="text-xs text-slate-500 mt-0.5">
          {(r.standard_ids || [r.standard_id]).join(", ")}{r.cross_standard ? " (cross-standard)" : ""}{r.focus?.term ? ` · Focus: ${r.focus.term}` : ""} · SME: {r.sme_name || "—"} · {r.question_count} question{r.question_count === 1 ? "" : "s"} · {r.answered_count} answered
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs">
        {r.findings?.pass > 0 && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200">{r.findings.pass} pass</span>}
        {r.findings?.gap > 0 && <span className="px-2 py-0.5 bg-red-50 text-red-700 border border-red-200">{r.findings.gap} gap</span>}
        {r.findings?.followup > 0 && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">{r.findings.followup} follow-up</span>}
      </div>
      <span data-testid={`rr-status-${r.id}`} className={`px-2 py-1 text-xs font-medium ${STATUS_STYLE[r.status] || ""}`}>{STATUS_LABEL[r.status] || r.status}</span>
      <Button data-testid={`rr-delete-${r.id}`} variant="ghost" size="sm" className="text-slate-400 hover:text-red-600"
              onClick={(e) => { e.stopPropagation(); onDelete(r); }}>
        <Trash size={16} />
      </Button>
      <CaretRight size={16} className="text-slate-400" />
    </div>
  );
}

export default function ClaroLens() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [standards, setStandards] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ standard_id: "", title: "", sme_name: "", sme_email: "" });
  const [creating, setCreating] = useState(false);
  const [mode, setMode] = useState("single"); // single | multi
  const [stdIds, setStdIds] = useState([]);
  const [selection, setSelection] = useState({});
  const [focus, setFocus] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: revs }, { data: stds }] = await Promise.all([
        api.get("/rapid-reviews"), api.get("/standards")]);
      setRows(revs);
      setStandards(stds || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load ClaroLens reviews");
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const multi = mode === "multi";
    if (!multi && !form.standard_id) { toast.error("Pick a standard"); return; }
    if (multi && !Object.keys(selection).length) { toast.error("Pick at least one requirement"); return; }
    setCreating(true);
    try {
      const body = multi
        ? { ...form, standard_id: null, requirement_ids: Object.keys(selection), part_selections: selection,
            focus_term: focus?.term || "", focus_functions: focus?.functions || [], focus_instructions: focus?.instructions || "" }
        : form;
      const { data } = await api.post("/rapid-reviews", body);
      toast.success("ClaroLens review created");
      setShowCreate(false);
      nav(`/claro-lens/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally { setCreating(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/rapid-reviews/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="claro-lens-page" className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Detective size={26} weight="duotone" /> ClaroLens</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Compliance-led due diligence: generate an Auditor's Lens question set per standard, curate it,
            walk the SME through it with the round-trip Excel workbook, then reconcile responses into RSAW
            narratives and evidence links.
          </p>
        </div>
        <Button data-testid="rr-new-btn" className="rounded-none" onClick={() => setShowCreate(true)}>
          <Plus size={16} className="mr-1" /> New ClaroLens Review
        </Button>
      </div>

      <DataResidencyNote />

      {rows === null ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div data-testid="rr-empty" className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">
          No ClaroLens reviews yet. Start one by selecting a standard and the SME you'll interview.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => <ReviewRow key={r.id} r={r} onOpen={(id) => nav(`/claro-lens/${id}`)} onDelete={del} />)}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="rr-create-dialog" className={`rounded-none max-h-[88vh] overflow-y-auto ${mode === "multi" ? "max-w-2xl" : ""}`}>
          <DialogHeader><DialogTitle>New ClaroLens Review</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Review scope</label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="rr-create-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">One standard (all applicable requirements)</SelectItem>
                  <SelectItem value="multi">Multiple standards (pick requirements)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mode === "multi" ? (
              <LensScopePicker stdIds={stdIds} setStdIds={setStdIds} selection={selection} setSelection={setSelection}
                focus={focus} setFocus={setFocus} />
            ) : (
            <div>
              <label className="text-xs font-medium text-slate-600">Standard</label>
              <Select value={form.standard_id} onValueChange={(v) => setForm((f) => ({ ...f, standard_id: v }))}>
                <SelectTrigger data-testid="rr-create-standard"><SelectValue placeholder="Select a standard…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {standards.map((s) => (
                    <SelectItem key={s.standard_id} value={s.standard_id}>{s.standard_id} — {s.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-600">Title (optional)</label>
              <Input data-testid="rr-create-title" value={form.title} placeholder="ClaroLens — CIP-004 readiness"
                     onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">SME name</label>
                <Input data-testid="rr-create-sme-name" value={form.sme_name}
                       onChange={(e) => setForm((f) => ({ ...f, sme_name: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">SME email (optional)</label>
                <Input data-testid="rr-create-sme-email" value={form.sme_email}
                       onChange={(e) => setForm((f) => ({ ...f, sme_email: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button data-testid="rr-create-submit" className="rounded-none" disabled={creating} onClick={create}>
              {creating ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
